// The v2 board's table (GRO-276).
//
// Sticky # and Account columns and a sticky header; the wrap scrolls both ways when the columns
// outgrow it. The list is split into Pinned / To work / Auto / Meeting booked / Hidden by thin
// dividers with counts. The Hidden section is collapsed by default and its rows are struck through. Pin and Hide sit in a hover
// menu inside the sticky Account cell, so they stay in reach however far the table scrolls.
// Rows open today's slide-over panel; ↑/↓ (or j/k) walk the list and the panel follows.
// Columns that do not fit the wrap drop in `columnFit.ts`'s order (as the v1 table does), so a
// laptop-width board loses Usage and the AE before it side-scrolls; the CSV keeps every column.

import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { SortableHeader } from './BookTable';
import type { BookColumn, CellContext } from './columns';
import { GROUP_BODY, GROUP_HIDDEN, GROUP_LABELS, nextPreference } from './boardPref';
import type { BoardPref } from './boardPref';
import { columnsThatFit, tableMinWidth } from './columnFit';
import type { RankedAccount } from './derived';
import type { SortState } from './sort';
import { readLocalFlag, writeLocalFlag } from '../lib/storage';

// Whether the Hidden section is open; closed when nothing is stored.
export const HIDDEN_OPEN_KEY = 'board.hiddenGroupOpen';

function RowMenu({
  row,
  canEdit,
  pending,
  onSetPref,
}: {
  row: RankedAccount;
  canEdit: boolean;
  pending: boolean;
  onSetPref: (accountId: string, preference: BoardPref) => void;
}) {
  const pref = row.board_pref ?? null;
  return (
    <span className="rowmenu">
      <button
        type="button"
        aria-pressed={pref === 'pinned'}
        disabled={!canEdit || pending}
        onClick={() => onSetPref(row.id, nextPreference(pref, 'pinned'))}
      >
        {pref === 'pinned' ? 'Unpin' : 'Pin'}
      </button>
      <button
        type="button"
        aria-pressed={pref === 'hidden'}
        disabled={!canEdit || pending}
        onClick={() => onSetPref(row.id, nextPreference(pref, 'hidden'))}
      >
        {pref === 'hidden' ? 'Unhide' : 'Hide'}
      </button>
    </span>
  );
}

// The neighbouring account row, skipping dividers.
function neighbourRow(row: HTMLTableRowElement, step: number): HTMLTableRowElement | null {
  let sibling: Element | null = row;
  do {
    sibling = step > 0 ? sibling.nextElementSibling : sibling.previousElementSibling;
  } while (sibling && !(sibling as HTMLElement).dataset.accountId);
  return sibling as HTMLTableRowElement | null;
}

// The handlers a row needs. One object per table, kept current through a ref, so every row's
// props stay identical between renders and memo() can skip it.
interface RowHandlers {
  onToggle: (accountId: string) => void;
  onKey: (event: KeyboardEvent<HTMLTableRowElement>, accountId: string) => void;
  onSetPref: (accountId: string, preference: BoardPref) => void;
}

// One account row. Memoised: the cell readings (why now, relationship, funding …) are worked out
// only when this row's data, the columns or the context change, not when another row opens.
const DenseRow = memo(function DenseRow({
  row,
  columns,
  context,
  isOpen,
  canEditPrefs,
  pendingPref,
  showRowMenu,
  handlers,
}: {
  row: RankedAccount;
  columns: BookColumn[];
  context: CellContext;
  isOpen: boolean;
  canEditPrefs: boolean;
  pendingPref: boolean;
  showRowMenu: boolean;
  handlers: RowHandlers;
}) {
  return (
    <tr
      className={'acct' + (isOpen ? ' open' : '') + (row.board_pref === 'hidden' ? ' pref-hidden' : '')}
      data-account-id={row.id}
      tabIndex={0}
      aria-label={`${row.account} — open account`}
      onClick={event => {
        if ((event.target as HTMLElement).closest('a, button')) return;
        handlers.onToggle(row.id);
      }}
      onKeyDown={event => handlers.onKey(event, row.id)}
    >
      {columns.map(column => (
        <td key={column.key} className={column.numeric ? 'num' : undefined} data-col={column.key}>
          {column.cell(row, context)}
          {column.key === 'account' && showRowMenu ? (
            <RowMenu row={row} canEdit={canEditPrefs} pending={pendingPref} onSetPref={handlers.onSetPref} />
          ) : null}
        </td>
      ))}
    </tr>
  );
});

export function DenseTable({
  rows,
  columns,
  context,
  sort,
  onSort,
  openAccountId,
  onToggleAccount,
  onSelectAccount,
  canEditPrefs,
  pendingPrefId,
  onSetPref,
  groupLabels = GROUP_LABELS,
  showRowMenu = true,
  tableClassName,
}: {
  rows: RankedAccount[];
  columns: BookColumn[];
  context: CellContext;
  sort: SortState;
  onSort: (key: string) => void;
  openAccountId: string | null;
  onToggleAccount: (accountId: string) => void;
  onSelectAccount: (accountId: string) => void;
  canEditPrefs: boolean;
  pendingPrefId: string | null;
  onSetPref: (accountId: string, preference: BoardPref) => void;
  // The divider text per `row.group`. The AE view (GRO-360) has its own four sections.
  groupLabels?: Record<number, string>;
  // Pin and Hide in the Account cell. Off where there is no board preference to set, as on the
  // read-only AE view.
  showRowMenu?: boolean;
  // An extra class on the table: `aetable` for the AE view's wrapping cells.
  tableClassName?: string;
}) {
  // The latest props, read by the stable handlers below.
  const latest = useRef({ openAccountId, onToggleAccount, onSelectAccount, onSetPref });
  latest.current = { openAccountId, onToggleAccount, onSelectAccount, onSetPref };

  const handlers = useMemo<RowHandlers>(() => ({
    onToggle: accountId => latest.current.onToggleAccount(accountId),
    onSetPref: (accountId, preference) => latest.current.onSetPref(accountId, preference),
    onKey: (event, accountId) => {
      if ((event.target as HTMLElement).closest('a, button')) return;

      // Enter or Space opens and closes the panel.
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        latest.current.onToggleAccount(accountId);
        return;
      }

      // Arrows and j/k walk the list; an open panel follows the focus.
      const step = event.key === 'ArrowDown' || event.key === 'j' ? 1
        : event.key === 'ArrowUp' || event.key === 'k' ? -1 : 0;
      if (!step) return;
      event.preventDefault();
      const next = neighbourRow(event.currentTarget, step);
      if (!next) return;
      next.focus();
      if (latest.current.openAccountId) latest.current.onSelectAccount(next.dataset.accountId!);
    },
  }), []);

  // The dividers stick just under the header, so the wrap carries the header's measured height.
  const wrapRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLTableSectionElement>(null);
  useEffect(() => {
    const wrap = wrapRef.current;
    const head = headRef.current;
    if (!wrap || !head) return;
    const observer = new ResizeObserver(() => {
      const headHeight = head.getBoundingClientRect().height;
      wrap.style.setProperty('--dense-head-height', `${headHeight}px`);
    });
    observer.observe(head);
    return () => observer.disconnect();
  }, []);

  // The wrap's width, for dropping the columns that do not fit it. Measured on the wrap, not the
  // viewport, so an open panel or an extension narrowing the page counts too.
  const [wrapWidth, setWrapWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const update = () => setWrapWidth(wrap.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);
  // Memoised, so the memoised rows do not all redraw on every render while a column is dropped.
  const shownColumns = useMemo(
    () => (wrapWidth == null ? columns : columnsThatFit(columns, wrapWidth)), [columns, wrapWidth]);

  // Count each pin / work / auto / booked / hidden group once, for the dividers.
  const groupCounts = new Map<number, number>();
  for (const row of rows) {
    const group = row.group ?? GROUP_BODY;
    groupCounts.set(group, (groupCounts.get(group) ?? 0) + 1);
  }

  // The Hidden section is collapsed unless this viewer opened it (remembered per browser). Only
  // the board's own sections collapse: the AE and oversight views number their groups differently.
  const [hiddenOpen, setHiddenOpen] = useState(() => readLocalFlag(HIDDEN_OPEN_KEY, false));
  const hiddenCollapsible = groupLabels === GROUP_LABELS;
  function toggleHidden() {
    const next = !hiddenOpen;
    setHiddenOpen(next);
    writeLocalFlag(HIDDEN_OPEN_KEY, next);
  }

  const body: ReactNode[] = [];
  let previousGroup: number | null = null;
  for (const row of rows) {
    // A thin divider wherever the group changes. It sticks under the header while its group
    // scrolls, and its label sticks to the left so it survives a side-scroll.
    const group = row.group ?? GROUP_BODY;
    const collapsible = hiddenCollapsible && group === GROUP_HIDDEN;
    if (group !== previousGroup) {
      body.push(
        <tr key={'group-' + group} className="groupdivider">
          <td colSpan={shownColumns.length}>
            {collapsible ? (
              <button type="button" className="grouplabel grouptoggle" aria-expanded={hiddenOpen}
                onClick={toggleHidden}>
                <span className="groupchevron" aria-hidden="true">{hiddenOpen ? '▾' : '▸'}</span>
                {groupLabels[group]} · <span className="n">{groupCounts.get(group)}</span>
              </button>
            ) : (
              <span className="grouplabel">{groupLabels[group]} <span className="n">{groupCounts.get(group)}</span></span>
            )}
          </td>
        </tr>,
      );
    }
    previousGroup = group;
    if (collapsible && !hiddenOpen) continue;

    body.push(
      <DenseRow
        key={row.id}
        row={row}
        columns={shownColumns}
        context={context}
        isOpen={openAccountId === row.id}
        canEditPrefs={canEditPrefs}
        pendingPref={pendingPrefId === row.id}
        showRowMenu={showRowMenu}
        handlers={handlers}
      />,
    );
  }

  return (
    <div ref={wrapRef} className="tablewrap densewrap" role="region" aria-label="Accounts">
      <table className={'densetable' + (tableClassName ? ' ' + tableClassName : '')} style={{ width: tableMinWidth(shownColumns) }}>
        <colgroup>
          {shownColumns.map(column => (
            // Account takes no <col> width: the table is at least the sum of the widths, so it
            // gets its own width when the table scrolls and all the slack when it doesn't.
            <col key={column.key} data-col={column.key} style={{ width: (column.flex ?? column.key === 'account') ? undefined : column.width }} />
          ))}
        </colgroup>
        <thead ref={headRef}>
          <tr>
            {shownColumns.map(column => (
              <SortableHeader key={column.key} column={column} sort={sort} onSort={onSort} />
            ))}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
}
